import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiSend, FiBell } from "react-icons/fi";
import toast from "react-hot-toast";
import { chatApi } from "../../api/chat";
import { ChatMessage as ChatMessageType } from "../../types";
import ChatMessage from "../chat/ChatMessage";
import JuliaActivity from "../chat/JuliaActivity";
import { useAuth } from "../../contexts/AuthContext";
import { CHARACTERS } from "../../assets/characters";

interface JuliaPanelProps {
  moduleName: string;
  contextMessage: string;
  pendingTasks?: number;
}

const JuliaPanel: React.FC<JuliaPanelProps> = ({
  moduleName,
  contextMessage,
  pendingTasks = 0,
}) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [conversationId, setConversationId] = useState<string | undefined>();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const hasConversation = messages.length > 0;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 96)}px`;
    }
  }, [inputValue]);

  const handleSend = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || isTyping) return;

    const userMessage: ChatMessageType = {
      id: Date.now().toString(),
      conversation_id: conversationId || "",
      role: "user",
      content: trimmed,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsTyping(true);

    try {
      const response = await chatApi.sendMessage(
        trimmed,
        conversationId,
        moduleName,
      );
      const d = response.data as {
        conversation_id?: string;
        conversation?: { id?: string };
        message: ChatMessageType;
      };
      setConversationId(String(d.conversation_id || d.conversation?.id || ""));
      setMessages((prev) => [...prev, d.message]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: (Date.now() + 1).toString(),
          conversation_id: conversationId || "",
          role: "assistant",
          content: "Désolé, une erreur est survenue. Veuillez réessayer.",
          created_at: new Date().toISOString(),
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const userName = user ? `${user.first_name} ${user.last_name}` : "Vous";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        gap: "0.75rem",
      }}
    >
      {/* En-tête compact + mascotte */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "0.5rem",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.65rem",
            minWidth: 0,
          }}
        >
          <img
            src={CHARACTERS.julia}
            alt=""
            aria-hidden
            style={{
              width: hasConversation ? 40 : 52,
              height: hasConversation ? 40 : 52,
              borderRadius: "50%",
              objectFit: "contain",
              objectPosition: "center bottom",
              background: "rgba(255,255,255,0.55)",
              flexShrink: 0,
              transition: "width 0.2s ease, height 0.2s ease",
            }}
          />
          <div style={{ minWidth: 0 }}>
            <p
              style={{
                margin: 0,
                fontSize: "var(--font-size-xs)",
                fontWeight: 700,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                color: "var(--color-primary)",
              }}
            >
              Assistant Julia
            </p>
            <p
              style={{
                margin: "0.15rem 0 0",
                fontSize: "0.8rem",
                color: "var(--color-text-secondary)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              Module {moduleName}
            </p>
          </div>
        </div>
        {pendingTasks > 0 && (
          <button
            type="button"
            onClick={() =>
              toast(
                `${pendingTasks} tâche${pendingTasks > 1 ? "s" : ""} en attente sur ce module`,
              )
            }
            title="Voir les tâches en attente"
            aria-label={`${pendingTasks} tâches en attente`}
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 36,
              height: 36,
              background: "var(--color-bg)",
              border: "1px solid var(--color-border-light)",
              borderRadius: "var(--radius-md)",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <FiBell
              size={18}
              style={{ color: "var(--color-text-secondary)" }}
            />
            <span
              style={{
                position: "absolute",
                top: -4,
                right: -4,
                minWidth: 18,
                height: 18,
                padding: "0 4px",
                borderRadius: "var(--radius-full)",
                background: "var(--color-danger)",
                color: "#fff",
                fontSize: "0.625rem",
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: 1,
              }}
            >
              {pendingTasks}
            </span>
          </button>
        )}
      </div>

      {/* Accueil : uniquement avant la conversation */}
      <AnimatePresence initial={false}>
        {!hasConversation && (
          <motion.div
            key="welcome"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, height: 0, margin: 0, overflow: "hidden" }}
            transition={{ duration: 0.2 }}
            style={{ flexShrink: 0 }}
          >
            <div
              style={{
                background: "var(--color-surface)",
                border: "1px solid rgba(14, 165, 233, 0.25)",
                borderLeft: "3px solid var(--color-brand)",
                borderRadius: "var(--radius-lg)",
                padding: "0.85rem 1rem",
                boxShadow: "0 4px 16px rgba(27, 42, 74, 0.06)",
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: "0.875rem",
                  color: "var(--color-text)",
                  lineHeight: 1.5,
                }}
              >
                Bienvenue dans{" "}
                <span style={{ color: "var(--color-brand)", fontWeight: 600 }}>
                  {moduleName}
                </span>
                . Comment puis-je vous aider ?
              </p>
              <p
                style={{
                  margin: "0.45rem 0 0",
                  fontSize: "0.8rem",
                  color: "var(--color-text-secondary)",
                  lineHeight: 1.5,
                }}
              >
                {contextMessage}
              </p>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "0.75rem",
              }}
            >
              <img
                src={CHARACTERS.julia}
                alt="Julia"
                style={{
                  width: "min(140px, 55%)",
                  height: "auto",
                  maxHeight: 160,
                  objectFit: "contain",
                }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Fil de conversation : prend tout l’espace restant */}
      <div
        ref={messagesContainerRef}
        style={{
          flex: 1,
          minHeight: hasConversation ? 160 : 0,
          overflowY: "auto",
          overflowX: "hidden",
          borderRadius: "var(--radius-lg)",
          background: hasConversation
            ? "rgba(255,255,255,0.55)"
            : "transparent",
          border: hasConversation
            ? "1px solid rgba(14, 165, 233, 0.15)"
            : "none",
          padding: hasConversation ? "0.35rem 0.25rem" : 0,
          WebkitOverflowScrolling: "touch",
        }}
      >
        {messages.map((msg) => (
          <ChatMessage
            key={msg.id}
            role={msg.role}
            content={msg.content}
            userName={userName}
            metadata={msg.metadata}
            compact
          />
        ))}
        {isTyping && <JuliaActivity active />}
        <div ref={messagesEndRef} />
      </div>

      {/* Saisie collée en bas */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: "0.45rem",
          background: "var(--color-surface)",
          border: "1px solid var(--color-border-light)",
          borderRadius: "var(--radius-xl)",
          padding: "0.4rem 0.5rem 0.4rem 0.9rem",
          boxShadow: "var(--shadow-xs)",
          flexShrink: 0,
        }}
      >
        <textarea
          ref={textareaRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Écrivez à Julia…"
          rows={1}
          disabled={isTyping}
          aria-label="Message pour Julia"
          style={{
            flex: 1,
            minWidth: 0,
            padding: "0.45rem 0",
            fontSize: "0.875rem",
            border: "none",
            background: "transparent",
            outline: "none",
            resize: "none",
            fontFamily: "var(--font-family)",
            lineHeight: 1.45,
            maxHeight: 96,
            color: "var(--color-text)",
          }}
        />
        <button
          type="button"
          onClick={handleSend}
          disabled={!inputValue.trim() || isTyping}
          aria-label="Envoyer"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 34,
            height: 34,
            borderRadius: "50%",
            border: "none",
            background: inputValue.trim()
              ? "var(--color-brand)"
              : "transparent",
            color: inputValue.trim() ? "#fff" : "var(--color-text-muted)",
            transition: "all var(--transition-fast)",
            flexShrink: 0,
            cursor: inputValue.trim() && !isTyping ? "pointer" : "default",
          }}
        >
          <FiSend size={14} />
        </button>
      </div>
    </div>
  );
};

export default JuliaPanel;
